import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["couponTypeSelect", "valueFields"];

  connect() {
    // Store original label texts when page loads
    this.storeOriginalLabels();

    // Initial setup based on current coupon type
    this.updateFieldSettings();
  }

  disconnect() {
    // Cleanup: Remove any custom CSS properties set on input wrappers
    this.queryFieldWrappers().forEach((wrapper) => {
      const inputWrapper = wrapper.querySelector(
        '[class*="form__input-wrapper"]'
      );
      if (inputWrapper) {
        inputWrapper.style.removeProperty("--symbol-left");
      }
    });
  }

  storeOriginalLabels() {
    // Store the original label text (from Rails i18n) in data attributes
    this.queryFieldWrappers().forEach((wrapper) => {
      const label = wrapper.querySelector("label");
      const input = wrapper.querySelector("input[data-valueinput=true]");

      if (!label || !input) return;

      const fieldName = input.name.match(/\[([^\]]+)\]$/)?.[1];
      if (!fieldName) return;

      // Store original label text
      label.setAttribute("data-original-text", label.textContent.trim());
      label.setAttribute("data-field-name", fieldName);
    });
  }

  updateFieldSettings() {
    const couponTypeSelect =
      this.couponTypeSelectTarget.querySelector("select");
    if (!couponTypeSelect) return;

    const selectedType = couponTypeSelect.value;

    switch (selectedType) {
      case "percent":
        this.setPercentSettings();
        this.updateLabels("percent");
        this.updateUnitDisplay("percent");
        this.setFieldsEnabled(true);
        break;

      case "amount":
        this.setAmountSettings();
        this.updateLabels("amount");
        this.updateUnitDisplay("amount");
        this.setFieldsEnabled(true);
        break;

      default:
        this.updateLabels(null);
        this.updateUnitDisplay(null);
        this.setFieldsEnabled(false);
    }
  }

  queryFields() {
    return this.valueFieldsTarget.querySelectorAll(
      "input[data-valueinput=true]"
    );
  }

  queryFieldWrappers() {
    return this.valueFieldsTarget.querySelectorAll('[class*="form__field"]');
  }

  setFieldsEnabled(enabled) {
    this.queryFields().forEach((field) => (field.disabled = !enabled));
  }

  setPercentSettings() {
    this.queryFields().forEach((field) => {
      field.min = 0;
      field.max = 100;
      field.step = 1;
    });
  }

  setAmountSettings() {
    this.queryFields().forEach((field) => {
      field.min = 0;
      field.max = 1200;
      field.step = 0.01;
    });
  }

  updateLabels(couponType) {
    this.queryFieldWrappers().forEach((wrapper) => {
      const label = wrapper.querySelector("label[data-original-text]");
      const input = wrapper.querySelector("input[data-valueinput=true]");

      if (!label || !input) return;

      const originalText = label.getAttribute("data-original-text");

      if (!couponType) {
        // Reset to original
        label.textContent = originalText;
        return;
      }

      // Replace "Wert" with the unit-specific text
      let newText = originalText;
      if (couponType === "percent") {
        newText = originalText.replace(/^Wert/, "Rabatt in %");
      } else if (couponType === "amount") {
        newText = originalText.replace(/^Wert/, "Rabatt in €");
      }

      label.textContent = newText;
    });
  }

  updateUnitDisplay(couponType) {
    this.queryFieldWrappers().forEach((wrapper) => {
      // Only update wrappers that have a value input (excludes duration field)
      const input = wrapper.querySelector("input[data-valueinput=true]");
      if (!input) return;

      // ViewComponent uses namespaced classes
      const inputWrapper = wrapper.querySelector(
        '[class*="form__input-wrapper"]'
      );
      if (!inputWrapper) return;

      if (couponType) {
        // Calculate position FIRST, before showing the symbol
        this.updateSymbolPositionForInput(input, inputWrapper);

        // Then set the data-unit attribute (which makes the symbol visible)
        // Use setTimeout to ensure position is set before symbol appears
        setTimeout(() => {
          inputWrapper.setAttribute("data-unit", couponType);
        }, 0);
      } else {
        inputWrapper.removeAttribute("data-unit");
      }
    });
  }

  updateSymbolPosition(event) {
    const input = event.target;
    const inputWrapper = input.closest('[class*="form__input-wrapper"]');
    if (inputWrapper) {
      this.updateSymbolPositionForInput(input, inputWrapper);
    }
  }

  updateSymbolPositionForInput(input, inputWrapper) {
    // Create a hidden span to measure text width
    const measureSpan = document.createElement("span");
    measureSpan.style.position = "absolute";
    measureSpan.style.visibility = "hidden";
    measureSpan.style.whiteSpace = "pre";
    measureSpan.style.font = window.getComputedStyle(input).font;
    measureSpan.textContent = input.value || "0";

    document.body.appendChild(measureSpan);
    const textWidth = measureSpan.offsetWidth;
    document.body.removeChild(measureSpan);

    // Get input padding-left
    const paddingLeft = parseFloat(window.getComputedStyle(input).paddingLeft);

    // Calculate position for the symbol (text width + padding + small gap)
    const symbolPosition = paddingLeft + textWidth + 4; // 4px gap

    // Set CSS custom property for the symbol position
    inputWrapper.style.setProperty("--symbol-left", `${symbolPosition}px`);
  }
}
